/**
 * 應收明細「依客戶分別列印」的版面(REQ-20260815012,REQ-20260817003 改成品項層級)。
 *
 * 明細表印出來是一張混了所有客戶的長清單,要拿去跟個別客戶請款時得自己剪。
 * 這裡把**同一份已經查回來的資料**依客戶重排:一位客戶一個區塊、各自有小計,
 * 列印時每位客戶從新的一頁開始(`globals.css` 的 `.print-customer-block`)。
 *
 * REQ-20260817003:這張是**對外跟客人請款**的表單,所以表身從「一單一列的帳務欄位」
 * 換成「一品項一列」(日期 / 單號 / 貨品類別 / 品名 / 數量 / 單位 / 金額),
 * 抬頭補上雙方基本資料。手法完全比照月結對帳單(`statements/StatementView.tsx`,
 * REQ-20260815013):同一張單第二個品項起日期與單號留白、沒有品項的歷史單仍印一列。
 *
 * 三個刻意的「不做」:
 *  - **不呼叫月結對帳單** `/statements/customer/:id`。那支綁單一月份、而且只結公帳
 *    (REQ-20260810002);明細表要保留任意日期區間、且含私帳單。兩者維持獨立。
 *  - **不加期初 / 期末滾動**。那是月結對帳單的職責(REQ-20260815012 §六),
 *    這裡印的是「這段期間開了哪些單、各是什麼品項」。
 *  - **沒有引進 PDF 套件**。同 REQ-20260806002 §3.4 / REQ-20260815002 §3.8 的取捨:
 *    瀏覽器 Cmd+P → 另存 PDF。
 *
 * 客戶的排列順序沿用清單原本的順序(Map 保序),不重新排序 ——
 * 畫面上看到的順序與印出來的順序一致才不會讓人以為查到的是別份資料。
 */
import type { ArApDetailRow, CompanyProfile } from '@app/shared';
import { formatMoney } from '../../lib/format-money';
import { useCompanyProfile } from '../accounting/api';
import { formatQty, printLines } from './print-lines';

export function GroupedCustomerStatements({ rows }: { rows: ArApDetailRow[] }): JSX.Element {
  const groups = groupByParty(rows);
  // 抬頭沒設定過(或這支查失敗)時就留白 —— 不擋住整張報表列印
  const { data: company } = useCompanyProfile();

  return (
    <div className="grid gap-6" data-testid="grouped-customer-statements">
      {groups.map((g, i) => (
        <CustomerBlock key={g[0].partyId} rows={g} company={company} first={i === 0} />
      ))}
    </div>
  );
}

/** 依 `partyId` 分組,順序 = 各客戶在原清單裡第一次出現的順序 */
function groupByParty(rows: ArApDetailRow[]): Array<[ArApDetailRow, ...ArApDetailRow[]]> {
  const map = new Map<number, [ArApDetailRow, ...ArApDetailRow[]]>();
  for (const row of rows) {
    const group = map.get(row.partyId);
    if (group) group.push(row);
    else map.set(row.partyId, [row]);
  }
  return [...map.values()];
}

function CustomerBlock({
  rows,
  company,
  first,
}: {
  rows: [ArApDetailRow, ...ArApDetailRow[]];
  company: CompanyProfile | undefined;
  first: boolean;
}): JSX.Element {
  const head = rows[0];
  const lines = rows.map((r) => ({ row: r, lines: printLines(r) }));
  const subtotal = lines.reduce(
    (sum, o) => o.lines.reduce((acc, l) => acc + Number(l.amount), sum),
    0,
  );

  return (
    // 第一個區塊不加 .print-customer-block,否則列印時會先吐一張空白頁
    <section
      className={`bg-white shadow-sm rounded-md p-4 ${first ? '' : 'print-customer-block'}`}
      data-testid="customer-block"
    >
      <header className="border-b pb-2">
        <div className="flex items-baseline justify-between gap-2">
          <h4 className="font-medium text-slate-900">
            {head.partyName}
            {head.partyCode ? (
              <span className="ml-2 text-slate-400 font-mono text-xs">{head.partyCode}</span>
            ) : null}
          </h4>
          <span className="text-xs text-slate-500 whitespace-nowrap">{rows.length} 張單據</span>
        </div>
        {/* 左欄客戶、右欄我方 —— 舊會計系統請款明細的抬頭排法 */}
        <div className="mt-2 grid gap-x-6 gap-y-0.5 text-xs sm:grid-cols-2">
          <Field label="客戶聯絡人" value={head.partyContact} />
          <Field label="本公司" value={company?.name} />
          <Field label="客戶電話" value={head.partyPhone} />
          <Field label="本公司地址" value={company?.address} />
          <Field label="客戶統一編號" value={head.partyTaxId} />
          <Field label="本公司電話" value={company?.phone} />
          <Field label="客戶地址" value={head.partyAddress} />
          <Field label="本公司傳真" value={company?.fax} />
        </div>
      </header>

      <table className="mt-2 w-full text-sm">
        <thead>
          <tr className="border-b text-left text-slate-600">
            <th className="px-2 py-1.5">日期</th>
            <th className="px-2 py-1.5">單號</th>
            <th className="px-2 py-1.5">貨品類別</th>
            <th className="px-2 py-1.5">品名</th>
            <th className="px-2 py-1.5 text-right">數量</th>
            <th className="px-2 py-1.5">單位</th>
            <th className="px-2 py-1.5 text-right">金額</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((o) =>
            o.lines.map((l, i) => (
              <tr key={l.key} className="border-b last:border-0">
                {/* 同一張單第二列起,日期與單號留白(比照舊系統的請款明細) */}
                <td className="px-2 py-1.5 whitespace-nowrap">{i === 0 ? o.row.txnDate : ''}</td>
                <td className="px-2 py-1.5 whitespace-nowrap font-mono">
                  {i === 0 ? o.row.orderNo : ''}
                </td>
                <td className="px-2 py-1.5">{l.categoryName ?? ''}</td>
                {/* 品名不加 whitespace-nowrap:長品名要能自己換行,不要把表格撐出紙外 */}
                <td className="px-2 py-1.5">
                  {l.productName ?? <span className="text-slate-500">（無品項明細）</span>}
                </td>
                {/* 數量是 DECIMAL(14,4),不能套 formatMoney(那支會四捨五入成整數) */}
                <td className="px-2 py-1.5 text-right font-mono">
                  {l.qty == null ? '' : formatQty(l.qty)}
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap">{l.unitName ?? ''}</td>
                <td className="px-2 py-1.5 text-right font-mono">{formatMoney(l.amount)}</td>
              </tr>
            )),
          )}
        </tbody>
        {/* 小計 = 上面金額欄的加總 = 該客戶各單 subtotal 的加總(未稅) */}
        <tfoot>
          <tr className="border-t-2 font-medium bg-slate-50">
            <td className="px-2 py-1.5" colSpan={6}>
              小計
            </td>
            <td className="px-2 py-1.5 text-right font-mono" data-testid="customer-subtotal">
              {formatMoney(subtotal)}
            </td>
          </tr>
        </tfoot>
      </table>
    </section>
  );
}

/** 抬頭的一格。沒填的欄位印「—」,與月結對帳單抬頭同一種寫法 */
function Field({ label, value }: { label: string; value: string | null | undefined }): JSX.Element {
  return (
    <div className="flex gap-2">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="text-slate-900">{value || '—'}</span>
    </div>
  );
}
